import { Button } from "../../../shared/components/ui/button"
import { useStepperContext } from "@/shared/context/stepper-context"
import React, { useState } from "react"
import { useFormContext } from "react-hook-form"
import { HomeWithHomeOwnerFormType } from "../schema/home-with-home-owner.schema"
import {
    createHomeOwner,
    createHomeWithHomeOwner,
} from "@/features/homeowners/api/homeowners.api"
import errorToast from "../../../shared/lib/toast/error.toast"
import successToast from "../../../shared/lib/toast/success.toast"
import { isEmailExists } from "@/features/auth/api/auth.api"
import {
    INVALID_PHONE_ERROR,
    USER_NOT_FOUND_ERROR,
    USER_ALREADY_EXISTS_ERROR,
} from "@/features/auth/constants/backend-errors"
import { PHONE_BACKEND_ERROR_MESSAGE, transformToE164 } from "@/shared/lib/utils/phone.utils"
import { EMAIL_ALREADY_EXISTS_ERROR } from "../../../shared/constants/error-messages"
import { useRouter } from "next/navigation"
import { HOMEOWNERS_ROUTES } from "../../../shared/constants/pages-routes"
import { useQueryClient } from "@tanstack/react-query"
import { HOME_OWNERS } from "../../../shared/constants/backend-api"
import { isUnknownLocationValue } from "../lib/unknown-location"
import { useRegionId } from "@/shared/hooks/use-region-id"

// Phone transformation is now handled by the phoneSchema in common.schema.ts

function HomeOwnerWithHomeFormNavigator() {
    const router = useRouter()

    const queryClient = useQueryClient()

    const {
        currentStep,
        setStepCompleted,
        setCurrentStep,
        totalNumberOfSteps,
    } = useStepperContext()

    const {
        trigger,
        getValues,
        formState: { errors },
        setError,
    } = useFormContext<HomeWithHomeOwnerFormType>()
    const [isLoading, setIsLoading] = useState(false)
    const { regionId } = useRegionId()

    const handleNext = () => {
        if (currentStep.number === 1) {
            handleSubmitForm()
        }
    }

    const increaseStep = () => {
        setStepCompleted(currentStep.number)
        setCurrentStep(currentStep.number + 1)
    }

    const handlePrevious = () => {
        setCurrentStep(currentStep.number - 1)
    }


    const handleSubmitForm = async () => {
        // Get form values once for validation checks
        const formValues = getValues()
        const homeForm1Data = formValues.home
        
        // Validate homeowner form first
        const isHomeOwnerValid = await trigger("homeOwner", { shouldFocus: true })
        if (!isHomeOwnerValid) {
            return
        }
        
        // Critical validation: Ensure address was selected from Google Maps autocomplete
        // Check this FIRST - if address exists but isn't normalized, show specific error
        // (latitude/longitude are only set when address is selected from Google Maps)
        // This prevents creating homeowner with non-normalized addresses
        const isUnknownLocation = isUnknownLocationValue(homeForm1Data?.address)
        if (homeForm1Data?.address && homeForm1Data.address.trim() !== "" && !isUnknownLocation) {
            // Address field has a value, check if it's normalized
            if (!homeForm1Data?.latitude || !homeForm1Data?.longitude) {
                const errorMessage =
                    "Either select a validated address from the dropdown or use the 'I don't have a street address' link to generate valid gps coordinates"
                setError(
                    "home.address",
                    { message: errorMessage },
                    { shouldFocus: true }
                )
                errorToast(errorMessage)
                return
            }
        }
        
        // Additional check: Ensure address, city, and state are not empty
        // (matching backend @IsNotEmpty() validation) - check AFTER normalization check
        // This handles the case where fields are completely empty
        if (!isUnknownLocation && (
            !homeForm1Data?.address || homeForm1Data.address.trim() === "" ||
            !homeForm1Data?.city || homeForm1Data.city.trim() === "" ||
            !homeForm1Data?.state || homeForm1Data.state.trim() === ""
        )) {
            const errorMessage = "Address, city, and state are required"
            setError(
                "home.address",
                { message: errorMessage },
                { shouldFocus: true }
            )
            errorToast(errorMessage)
            return
        }

        if (isUnknownLocation && !regionId) {
            errorToast("Unable to determine the current region. Please refresh and try again.")
            return
        }

        // Validate only the home fields we're keeping (address, city, state, projectGeneralNotes, repairabilityInfo)
        // We only validate the essential fields and the ones we're showing on this page
        const essentialHomeKeys = ['address', 'city', 'state', 'country', 'zipCode', 'latitude', 'longitude', 'projectGeneralNotes']
        const essentialHomeFormattedKeys = essentialHomeKeys.map((key) => `home.${key}`)
        const isHomeValid = await trigger(essentialHomeFormattedKeys as any, {
            shouldFocus: true,
        })
        
        if (!isHomeValid) {
            return
        }

        // All validations passed, proceed with creation
        setIsLoading(true)
        
        // Transform phone numbers to E.164 format for backend
        if (formValues.homeOwner) {
            if (formValues.homeOwner.phone) {
                formValues.homeOwner.phone = transformToE164(formValues.homeOwner.phone) || undefined
            }
            if (formValues.homeOwner.secondaryPhone) {
                formValues.homeOwner.secondaryPhone = transformToE164(formValues.homeOwner.secondaryPhone) || undefined
            } else {
                delete formValues.homeOwner.secondaryPhone
            }
        }
        
        const homeWithHomeOwner = await createHomeWithHomeOwner({
            ...formValues,
            home: {
                ...formValues.home,
                ...(regionId != null ? { region: String(regionId) } : {}),
                ...(isUnknownLocation
                    ? { latitude: null as any, longitude: null as any, city: "", state: "", zipCode: "" }
                    : {}),
            },
        })
        setIsLoading(false)
        if (!homeWithHomeOwner.success) {
            // Check for phone validation errors - backend may return multiple error messages
            // Look for phone-related error messages in the error response
            const errorMessage = homeWithHomeOwner.message || ''
            const normalizedErrorMessage = errorMessage.toLowerCase()
            const isPhoneError = 
                errorMessage.includes('phone') && (
                    errorMessage.includes('Invalid phone number') ||
                    errorMessage.includes('phone must be a string') ||
                    errorMessage.includes('phone should not be empty') ||
                    errorMessage === INVALID_PHONE_ERROR
                )
            const isDuplicateAddressError =
                normalizedErrorMessage.includes('duplicate key value violates unique constraint') &&
                normalizedErrorMessage.includes('homes_address')
            
            if (isPhoneError) {
                setError(
                    "homeOwner.phone",
                    { message: "Please provide a valid phone number" },
                    { shouldFocus: true }
                )
                errorToast("Please provide a valid phone number")
                return
            }
            if (isDuplicateAddressError) {
                const message = "A homeowner already exists for this address. Please verify the address or update the existing record."
                setError(
                    "home.address",
                    { message },
                    { shouldFocus: true }
                )
                errorToast(message)
                return
            }
            if (homeWithHomeOwner.message === USER_ALREADY_EXISTS_ERROR) {
                setError(
                    "homeOwner.email",
                    { message: "There is already an account with that email" },
                    { shouldFocus: true }
                )
                return
            }
            errorToast(homeWithHomeOwner.message)
            return
        }
        queryClient.invalidateQueries({ queryKey: [HOME_OWNERS.base] })
        successToast("Homeowner created successfully")
        
        // Get homeowner ID from response and redirect to home details page
        const homeownerId = (homeWithHomeOwner as any).data?.homeownerId || (homeWithHomeOwner as any).data?.id
        if (homeownerId) {
            const detailsHref = HOMEOWNERS_ROUTES.details(homeownerId) + '?tab=home'
            router.push(detailsHref)
        } else {
            // Fallback to homeowners list if ID not available
            router.push(HOMEOWNERS_ROUTES.base)
        }
    }

    return (
        <div className="flex justify-end gap-4">
            {currentStep.number > 1 && (
                <Button
                    className="h-14 w-[250px]"
                    variant={"outline"}
                    onClick={handlePrevious}
                >
                    Previous
                </Button>
            )}
            <Button
                type="button"
                onClick={handleNext}
                className="h-14 w-[250px]"
                disabled={isLoading}
                showLoader={isLoading}
            >
                {currentStep.number === totalNumberOfSteps ? "Submit" : "Next"}
            </Button>
        </div>
    )
}

export default HomeOwnerWithHomeFormNavigator
